import React from 'react';
import { localizedValue } from './nationalizationViewModel';

export default function NationalizationRecommendations({ recommendations = [], statusCode, language, t }) {
  const items = Array.isArray(recommendations) ? recommendations : [];

  const priorityLabel = (priority) => {
    const numeric = Number(priority);
    if (Number.isFinite(numeric)) {
      if (numeric >= 70) return t('recommendations.priority.high', 'High priority');
      if (numeric >= 40) return t('recommendations.priority.medium', 'Medium priority');
      return t('recommendations.priority.low', 'Low priority');
    }
    const code = String(priority || 'normal').toLowerCase();
    return t(`recommendations.priority.${code}`, code);
  };

  const expectedImpactLabel = (impact) => {
    if (!impact) return '';
    if (typeof impact === 'string' || typeof impact === 'number') return String(impact);
    if (impact.description || impact.label) return impact.description || impact.label;
    if (impact.gap_reduction !== undefined) {
      return t('recommendations.impact.gapReduction', 'Reduce the gap by {{count}}', { count: impact.gap_reduction });
    }
    if (impact.rate_change_pct !== undefined) {
      return t('recommendations.impact.rateChange', 'Estimated change: {{value}}%', { value: impact.rate_change_pct });
    }
    if (impact.qualifying_count_change !== undefined) {
      return t('recommendations.impact.qualifyingCount', '{{count}} additional qualifying employees', {
        count: impact.qualifying_count_change
      });
    }
    if (impact.modeled_gap_reduction_max !== undefined) {
      return t('recommendations.impact.gapReduction', 'Reduce the gap by {{count}}', {
        count: impact.modeled_gap_reduction_max
      });
    }
    return '';
  };

  return (
    <section className="nationalization-recommendations" aria-labelledby="nationalization-recommendations-title">
      <h3 id="nationalization-recommendations-title" className="nationalization-recommendations-title">
        {t('recommendations.title', 'Recommendations')}
        <span>{items.length}</span>
      </h3>

      {items.length ? (
        <div className="nationalization-recommendations-grid">
          {items.map((recommendation, index) => {
            const code = recommendation?.code || `recommendation_${index + 1}`;
            const localeCode = code.replace(/^nationalization\.recommendation\./, '');
            const params = recommendation?.params || {};
            const employeeCount = params.employee_count;
            const gapCount = params.gap_count ?? params.current_gap_count;
            const title = t(`recommendations.items.${localeCode}.title`, {
              ...params,
              defaultValue: localizedValue(recommendation, language, 'title') || code
            });
            let descriptionKey = `recommendations.items.${localeCode}.description`;
            if (localeCode === 'complete_employee_evidence' && employeeCount !== undefined) {
              descriptionKey = `recommendations.items.${localeCode}.descriptionWithEmployeeCount`;
            } else if (localeCode === 'hire_qualifying_local_nationals' && gapCount !== undefined) {
              descriptionKey = `recommendations.items.${localeCode}.descriptionWithGapCount`;
            }
            let description = t(descriptionKey, {
              ...params,
              gap_count: gapCount,
              defaultValue: localizedValue(recommendation, language, 'description') || ''
            });
            // Keep any backend-supplied workforce/gap number visible even for a
            // future recommendation code that does not yet have a special copy.
            if (
              descriptionKey.endsWith('.description')
              && (employeeCount !== undefined || gapCount !== undefined)
            ) {
              description = [
                description,
                t('recommendations.countContext', '{{count}} employees affected.', {
                  count: employeeCount ?? gapCount
                })
              ].filter(Boolean).join(' ');
            }
            const priority = priorityLabel(recommendation?.priority);
            const expectedImpact = expectedImpactLabel(recommendation?.expected_impact);

            return (
              <article
                key={`${code}-${index}`}
                aria-label={[priority, expectedImpact].filter(Boolean).join('. ')}
              >
                <p>{[title, description].filter(Boolean).join(' — ')}</p>
              </article>
            );
          })}
        </div>
      ) : (
        <p className="nationalization-empty-copy">
          {statusCode === 'not_applicable'
            ? t('recommendations.emptyNotApplicable', 'No action recommendations are needed for this verified exemption.')
            : t('recommendations.empty', 'No recommendations are available until the first complete calculation.')}
        </p>
      )}
    </section>
  );
}
